import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  BookOpen, 
  Plus, 
  Search,
  MoreVertical,
  Pencil,
  Trash2,
  TrendingUp,
  TrendingDown,
  Building2,
  Landmark
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const typeColors = {
  asset: 'bg-blue-50 text-blue-700 border-blue-200',
  liability: 'bg-red-50 text-red-700 border-red-200',
  equity: 'bg-purple-50 text-purple-700 border-purple-200',
  revenue: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  expense: 'bg-amber-50 text-amber-700 border-amber-200'
};

const ChartOfAccounts = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { currencySymbol } = useSettings();
  
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({});
  const [filterType, setFilterType] = useState('all');
  const [search, setSearch] = useState('');

  const typeLabels = {
    asset: t('pages.chartOfAccounts.asset'),
    liability: t('pages.chartOfAccounts.liability'),
    equity: t('pages.chartOfAccounts.equity'),
    revenue: t('pages.chartOfAccounts.revenue'),
    expense: t('pages.chartOfAccounts.expense')
  };

  const categories = [
    { value: 'cash', label: t('pages.chartOfAccounts.categories.cash') },
    { value: 'bank', label: t('pages.chartOfAccounts.categories.bank') },
    { value: 'receivable', label: t('pages.chartOfAccounts.categories.receivable') },
    { value: 'inventory', label: t('pages.chartOfAccounts.categories.inventory') },
    { value: 'fixed_asset', label: t('pages.chartOfAccounts.categories.fixedAsset') },
    { value: 'payable', label: t('pages.chartOfAccounts.categories.payable') },
    { value: 'loan', label: t('pages.chartOfAccounts.categories.loan') },
    { value: 'capital', label: t('pages.chartOfAccounts.categories.capital') },
    { value: 'retained', label: t('pages.chartOfAccounts.categories.retained') },
    { value: 'sales', label: t('pages.chartOfAccounts.categories.sales') },
    { value: 'service', label: t('pages.chartOfAccounts.categories.service') },
    { value: 'operating', label: t('pages.chartOfAccounts.categories.operating') },
    { value: 'administrative', label: t('pages.chartOfAccounts.categories.administrative') },
    { value: 'other', label: t('pages.chartOfAccounts.categories.other') }
  ];

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/accounts/chart`);
      setAccounts(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingAccount) {
        await axios.put(`${API_URL}/api/accounts/chart/${editingAccount.id}`, formData);
        toast.success(t('pages.chartOfAccounts.accountUpdated'));
      } else {
        await axios.post(`${API_URL}/api/accounts/chart`, formData);
        toast.success(t('pages.chartOfAccounts.accountCreated'));
      }
      setShowDialog(false);
      setEditingAccount(null);
      setFormData({});
      fetchAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('pages.chartOfAccounts.failedToSave'));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('pages.chartOfAccounts.confirmDelete'))) {
      try {
        await axios.delete(`${API_URL}/api/accounts/chart/${id}`);
        toast.success(t('pages.chartOfAccounts.accountDeleted'));
        fetchAccounts();
      } catch (err) {
        toast.error(t('pages.chartOfAccounts.failedToDelete'));
      }
    }
  };

  const openEdit = (account) => {
    setEditingAccount(account);
    setFormData({ ...account });
    setShowDialog(true);
  };

  const openNew = () => {
    setEditingAccount(null);
    setFormData({ type: 'asset', category: 'cash', currency: 'USD' });
    setShowDialog(true);
  };

  const filteredAccounts = accounts
    .filter(a => filterType === 'all' || a.type === filterType)
    .filter(a => !search || a.name.toLowerCase().includes(search.toLowerCase()) || a.code.includes(search));

  const stats = {
    assets: accounts.filter(a => a.type === 'asset').reduce((sum, a) => sum + (a.balance || 0), 0),
    liabilities: accounts.filter(a => a.type === 'liability').reduce((sum, a) => sum + (a.balance || 0), 0),
    equity: accounts.filter(a => a.type === 'equity').reduce((sum, a) => sum + (a.balance || 0), 0),
    revenue: accounts.filter(a => a.type === 'revenue').reduce((sum, a) => sum + (a.balance || 0), 0),
    expenses: accounts.filter(a => a.type === 'expense').reduce((sum, a) => sum + (a.balance || 0), 0)
  };

  return (
    <div className={`space-y-6 ${isRTL ? 'text-right' : ''}`}>
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('pages.chartOfAccounts.title')}</h1>
          <p className="text-muted-foreground">{t('pages.chartOfAccounts.subtitle')}</p>
        </div>
        <Button onClick={openNew} className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><Plus className="h-4 w-4" /> {t('pages.chartOfAccounts.addAccount')}</Button>
      </div>

      <div className={`grid grid-cols-5 gap-4 ${isRTL ? 'direction-rtl' : ''}`}>
        <Card><CardContent className="pt-6"><div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><TrendingUp className="h-5 w-5 text-blue-500" /><div><div className="text-xl font-bold">{currencySymbol} {stats.assets.toLocaleString()}</div><div className="text-xs text-muted-foreground">{t('pages.chartOfAccounts.assets')}</div></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><TrendingDown className="h-5 w-5 text-red-500" /><div><div className="text-xl font-bold">{currencySymbol} {stats.liabilities.toLocaleString()}</div><div className="text-xs text-muted-foreground">{t('pages.chartOfAccounts.liabilities')}</div></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><Building2 className="h-5 w-5 text-purple-500" /><div><div className="text-xl font-bold">{currencySymbol} {stats.equity.toLocaleString()}</div><div className="text-xs text-muted-foreground">{t('pages.chartOfAccounts.equity')}</div></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><TrendingUp className="h-5 w-5 text-emerald-500" /><div><div className="text-xl font-bold">{currencySymbol} {stats.revenue.toLocaleString()}</div><div className="text-xs text-muted-foreground">{t('pages.chartOfAccounts.revenue')}</div></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><TrendingDown className="h-5 w-5 text-amber-500" /><div><div className="text-xl font-bold">{currencySymbol} {stats.expenses.toLocaleString()}</div><div className="text-xs text-muted-foreground">{t('pages.chartOfAccounts.expenses')}</div></div></div></CardContent></Card>
      </div>

      <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className="relative flex-1 max-w-sm">
          <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground`} />
          <Input className={isRTL ? 'pr-10' : 'pl-10'} placeholder={t('pages.chartOfAccounts.searchPlaceholder')} value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-40"><SelectValue placeholder={t('pages.common.filter')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('pages.chartOfAccounts.allTypes')}</SelectItem>
            {Object.entries(typeLabels).map(([key, label]) => (
              <SelectItem key={key} value={key}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.chartOfAccounts.code')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.chartOfAccounts.accountName')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.chartOfAccounts.type')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.chartOfAccounts.category')}</TableHead>
              <TableHead className={`${isRTL ? 'text-left' : 'text-right'}`}>{t('pages.chartOfAccounts.balance')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.chartOfAccounts.status')}</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAccounts.map((account) => (
              <TableRow key={account.id}>
                <TableCell className="font-mono font-medium">{account.code}</TableCell>
                <TableCell>
                  <div className="font-medium">{account.name}</div>
                  {account.description && <div className="text-xs text-muted-foreground">{account.description}</div>}
                </TableCell>
                <TableCell><Badge className={typeColors[account.type]}>{typeLabels[account.type]}</Badge></TableCell>
                <TableCell className="text-muted-foreground capitalize">{account.category?.replace('_', ' ')}</TableCell>
                <TableCell className={`${isRTL ? 'text-left' : 'text-right'} font-mono font-semibold`}>
                  <span className={account.balance < 0 ? 'text-red-600' : ''}>
                    {currencySymbol} {Math.abs(account.balance || 0).toLocaleString()}
                  </span>
                </TableCell>
                <TableCell>
                  <Badge variant={account.is_active ? 'default' : 'secondary'}>
                    {account.is_active ? t('pages.chartOfAccounts.activeStatus') : t('pages.chartOfAccounts.inactive')}
                  </Badge>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align={isRTL ? 'start' : 'end'}>
                      <DropdownMenuItem onClick={() => openEdit(account)}><Pencil className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.common.edit')}</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(account.id)}>
                        <Trash2 className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.common.delete')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {filteredAccounts.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="h-32 text-center">
                  <BookOpen className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-muted-foreground">{t('pages.chartOfAccounts.noAccounts')}</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingAccount ? t('pages.chartOfAccounts.editAccount') : t('pages.chartOfAccounts.addAccount')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pages.chartOfAccounts.accountCode')} *</Label>
                <Input value={formData.code || ''} onChange={e => setFormData({...formData, code: e.target.value})} placeholder="1000" />
              </div>
              <div className="space-y-2">
                <Label>{t('pages.chartOfAccounts.type')} *</Label>
                <Select value={formData.type || 'asset'} onValueChange={v => setFormData({...formData, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(typeLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('pages.chartOfAccounts.accountName')} *</Label>
              <Input value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Cash on Hand" />
            </div>

            <div className="space-y-2">
              <Label>{t('pages.chartOfAccounts.category')}</Label>
              <Select value={formData.category || 'other'} onValueChange={v => setFormData({...formData, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('pages.chartOfAccounts.description')}</Label>
              <Textarea value={formData.description || ''} onChange={e => setFormData({...formData, description: e.target.value})} rows={2} />
            </div>
          </div>
          <DialogFooter className={isRTL ? 'flex-row-reverse' : ''}>
            <Button variant="outline" onClick={() => setShowDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave}>{editingAccount ? t('pages.common.edit') : t('pages.common.add')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChartOfAccounts;
